import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPhysicalItems, getProcessEvents } from '../processes.api';
import type { CanonicalProcessName, PhysicalItemsFilters } from '../processes.api';

const PHYSICAL_ITEMS_QUERY_KEY = ['processes', 'physical-items'] as const;

/** Server-state voor de productlijst van één proces (brok 3 van het detailscherm). */
export function useProcessItems(name: CanonicalProcessName, page: number, step?: string, filters: PhysicalItemsFilters = {}, kolommen?: Record<string, string>) {
  return useQuery({
    // De filters in de sleutel: anders toont een andere klant het gecachte antwoord van de vorige.
    queryKey: [...PHYSICAL_ITEMS_QUERY_KEY, name, page, step ?? null, filters.gln ?? null, filters.stilstandDrempelWerkdagen ?? null, kolommen ?? null],
    queryFn: () => getPhysicalItems({ name, page, step, ...filters, kolommen }),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

/** Eigen ververs-actie per proces (AD-22): raakt uitsluitend de scan van dít proces. */
export function useRefreshProcessItems(name: CanonicalProcessName, page: number) {
  const queryClient = useQueryClient();
  return async () => {
    await getPhysicalItems({ name, page, refresh: true });
    await queryClient.invalidateQueries({ queryKey: [...PHYSICAL_ITEMS_QUERY_KEY, name] });
  };
}

/**
 * Server-state voor de productlijst van het fysieke proces (R3), server-gepagineerd; `step` filtert op de huidige stap.
 * `kolommen` (spec-tabelfilters story 8): kolomfilters, sortering en facetten als queryparameters.
 */
export function usePhysicalItems(page: number, filters: PhysicalItemsFilters = {}, step?: string, kolommen?: Record<string, string>) {
  return useQuery({
    queryKey: [...PHYSICAL_ITEMS_QUERY_KEY, page, filters.gln ?? null, filters.stilstandDrempelWerkdagen ?? null, step ?? null, kolommen ?? null],
    queryFn: () => getPhysicalItems({ page, step, ...filters, kolommen }),
    // Bij het bladeren de vorige pagina laten staan: anders unmount het hele blok — inclusief de knop
    // waarop net geklikt is — en springt de toetsenbordfocus terug naar de pagina-body.
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

/**
 * Eigen ververs-actie (AD-22): deze pagina hangt aan de zware gebeurtenissen-scan met een eigen,
 * langere TTL. De ververs-knop van het overzicht raakt die bewust niet, en andersom.
 */
export function useRefreshPhysicalItems(page: number) {
  const queryClient = useQueryClient();
  return async () => {
    await getPhysicalItems({ page, refresh: true });
    await queryClient.invalidateQueries({ queryKey: PHYSICAL_ITEMS_QUERY_KEY });
  };
}

/**
 * De gebeurtenissenstroom van één proces. `enabled` houdt het tweede tabblad gratis tot iemand het
 * opent — de stroom leest weliswaar uit dezelfde gecachte scan, maar een koude cache kost seconden.
 */
export function useProcessEvents(name: CanonicalProcessName, enabled: boolean) {
  return useQuery({
    queryKey: ['processes', 'events', name],
    queryFn: () => getProcessEvents(name),
    enabled,
    staleTime: 30 * 1000,
  });
}
